/*
  Commande groupée B2B — Bulle de douceur

  Le JS n'invente aucun prix : il lit le prix rendu par Liquid (data-price, en
  centimes, déjà résolu dans le catalogue B2B du client) et se contente
  d'additionner. Le total affiché ne peut donc pas diverger du panier.

  Un seul appel POST /cart/add.js pour l'ensemble des lignes saisies.
*/

class B2BBulkOrder extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-b2b-row]'));
    if (!this.rows.length) return;

    this.searchInput = this.querySelector('[data-b2b-search]');
    this.countEl = this.querySelector('[data-b2b-count]');
    this.emptyEl = this.querySelector('[data-b2b-empty]');
    this.moreBtn = this.querySelector('[data-b2b-more]');
    this.submitBtn = this.querySelector('[data-b2b-submit]');
    this.grandTotalEl = this.querySelector('[data-b2b-grand-total]');
    this.totalLabelEl = this.querySelector('[data-b2b-total-label]');
    this.feedbackEl = this.querySelector('[data-b2b-feedback]');

    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.batchSize = parseInt(this.dataset.batch, 10) || 100;
    this.revealed = this.batchSize;
    this.term = '';

    this.formatter = this.buildFormatter();

    this.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('change', this.onInput.bind(this));
    if (this.moreBtn) this.moreBtn.addEventListener('click', () => this.showMore());
    if (this.submitBtn) this.submitBtn.addEventListener('click', () => this.submit());

    this.applyVisibility();
    this.recalc();
  }

  buildFormatter() {
    const currency = this.dataset.currency || 'EUR';
    const locale = this.dataset.locale || 'fr';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (e) {
      return new Intl.NumberFormat('fr', { style: 'currency', currency: 'EUR' });
    }
  }

  money(cents) {
    return this.formatter.format(cents / 100);
  }

  onInput(event) {
    if (event.target.matches('[data-b2b-search]')) {
      this.term = event.target.value.trim().toLowerCase();
      this.applyVisibility();
      return;
    }
    if (event.target.matches('[data-b2b-qty]')) {
      this.recalc();
    }
  }

  /* ---------- Validation ---------- */

  readRow(row) {
    const input = row.querySelector('[data-b2b-qty]');
    const raw = input ? input.value.trim() : '';
    const min = parseInt(row.dataset.min, 10) || 1;
    const step = parseInt(row.dataset.step, 10) || 1;
    const max = parseInt(row.dataset.max, 10) || 0;
    const available = row.dataset.available === 'true';
    const price = parseInt(row.dataset.price, 10) || 0;

    if (raw === '') return { qty: 0, price, error: null };

    const qty = Number(raw);

    if (!Number.isInteger(qty) || qty < 0) {
      return { qty: 0, price, error: 'Quantité invalide' };
    }
    if (qty === 0) return { qty: 0, price, error: null };
    if (!available) {
      return { qty, price, error: 'Produit en rupture' };
    }
    if (qty < min) {
      return { qty, price, error: `Minimum ${min}` };
    }
    if (max > 0 && qty > max) {
      return { qty, price, error: `Maximum ${max}` };
    }
    if (step > 1 && qty % step !== 0) {
      return { qty, price, error: `Par multiples de ${step}` };
    }
    return { qty, price, error: null };
  }

  recalc() {
    let total = 0;
    let lines = 0;
    let units = 0;
    let hasError = false;

    this.rows.forEach((row) => {
      const { qty, price, error } = this.readRow(row);
      const errorEl = row.querySelector('[data-b2b-row-error]');
      const lineEl = row.querySelector('[data-b2b-line-total]');

      if (errorEl) errorEl.textContent = error || '';
      row.classList.toggle('b2b-order__row--invalid', Boolean(error));
      if (error) hasError = true;

      if (!error && qty > 0) {
        const lineTotal = qty * price;
        total += lineTotal;
        lines += 1;
        units += qty;
        if (lineEl) lineEl.textContent = this.money(lineTotal);
      } else if (lineEl) {
        lineEl.textContent = '—';
      }
    });

    this.grandTotalEl.textContent = lines ? this.money(total) : '—';

    if (!lines) {
      this.totalLabelEl.textContent = 'Total HT · aucune ligne saisie';
    } else {
      const l = lines > 1 ? 'lignes' : 'ligne';
      const u = units > 1 ? 'articles' : 'article';
      this.totalLabelEl.textContent = `Total HT · ${lines} ${l}, ${units} ${u} · TVA en sus`;
    }

    this.submitBtn.disabled = lines === 0 || hasError;
    if (hasError) {
      this.setFeedback('Corrigez les lignes signalées avant d’ajouter au panier.', true);
    } else {
      this.setFeedback('');
    }
  }

  setFeedback(message, isError) {
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = message;
    this.feedbackEl.classList.toggle('b2b-order__feedback--error', Boolean(isError));
  }

  /* ---------- Recherche et affichage progressif ---------- */

  applyVisibility() {
    let matches = 0;
    let shown = 0;

    this.rows.forEach((row) => {
      const isMatch = !this.term || (row.dataset.search || '').indexOf(this.term) !== -1;
      if (!isMatch) {
        row.hidden = true;
        return;
      }
      matches += 1;
      // Pendant une recherche on montre tous les résultats ; sinon on limite
      // au lot courant pour ne pas alourdir le rendu.
      const withinBatch = this.term !== '' || shown < this.revealed;
      row.hidden = !withinBatch;
      if (withinBatch) shown += 1;
    });

    if (this.emptyEl) this.emptyEl.classList.toggle('hidden', matches > 0);

    if (this.moreBtn) {
      const remaining = this.term === '' ? matches - shown : 0;
      this.moreBtn.classList.toggle('hidden', remaining <= 0);
      if (remaining > 0) {
        this.moreBtn.textContent = `Afficher plus de produits (${remaining} restants)`;
      }
    }

    if (this.countEl) {
      if (this.term) {
        this.countEl.textContent = `${matches} résultat${matches > 1 ? 's' : ''}`;
      } else {
        this.countEl.textContent = `${this.rows.length} référence${this.rows.length > 1 ? 's' : ''}`;
      }
    }
  }

  showMore() {
    this.revealed += this.batchSize;
    this.applyVisibility();
  }

  /* ---------- Ajout au panier ---------- */

  async submit() {
    const items = [];
    let blocked = false;

    this.rows.forEach((row) => {
      const { qty, error } = this.readRow(row);
      if (error) blocked = true;
      if (!error && qty > 0) {
        items.push({ id: Number(row.dataset.variantId), quantity: qty });
      }
    });

    if (blocked) {
      this.setFeedback('Corrigez les lignes signalées avant d’ajouter au panier.', true);
      return;
    }
    if (!items.length) {
      this.setFeedback('Saisissez au moins une quantité.', true);
      return;
    }

    this.submitBtn.disabled = true;
    this.setFeedback('Ajout en cours…');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      const payload = await response.json();

      if (!response.ok) {
        this.handleServerError(payload);
        this.submitBtn.disabled = false;
        return;
      }

      const count = items.length;
      this.setFeedback(
        `${count} ligne${count > 1 ? 's' : ''} ajoutée${count > 1 ? 's' : ''} au panier.`
      );
      this.refreshCartCount();
      this.submitBtn.disabled = false;
    } catch (error) {
      this.setFeedback(
        'L’ajout au panier a échoué. Vérifiez votre connexion et réessayez.',
        true
      );
      this.submitBtn.disabled = false;
    }
  }

  handleServerError(payload) {
    const message =
      (payload && (payload.description || payload.message)) ||
      'L’ajout au panier a été refusé.';

    // /cart/add.js est atomique : si une ligne est refusée, rien n'est ajouté.
    // On tente de retrouver la ligne fautive pour la signaler visuellement.
    const culprit = this.rows.find((row) => {
      const label = row.dataset.label || '';
      return label && message.indexOf(label.split(' — ')[0]) !== -1;
    });

    if (culprit) {
      const errorEl = culprit.querySelector('[data-b2b-row-error]');
      if (errorEl) errorEl.textContent = 'Ligne refusée';
      culprit.classList.add('b2b-order__row--invalid');
      culprit.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    this.setFeedback(`${message} Aucun article n’a été ajouté.`, true);
  }

  async refreshCartCount() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.querySelectorAll('.cart-count-bubble').forEach((el) => {
        el.classList.remove('hidden');
      });
    } catch (error) {
      // Le compteur d'en-tête n'est pas critique : on ignore silencieusement.
    }
  }
}

if (!customElements.get('b2b-bulk-order')) {
  customElements.define('b2b-bulk-order', B2BBulkOrder);
}
